/**
 * AdminCreatorPage - AI Game Creator administration dashboard.
 *
 * M2: Allows a human operator to input a Game Spec, trigger AI generation,
 * view the resulting GameArtifact, inspect Build/QA status, view failure
 * reasons and repair attempts, and perform human-only actions: Approve,
 * Publish, and Rollback.
 *
 * AI can NEVER approve or publish — those buttons send human_approved: true
 * with an approver_id, enforcing the Human Approval Gate at the API level.
 *
 * Route: /admin/creator
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { creatorApi, gamesApi, versionsApi } from '../lib/apiClient';

const TEMPLATES = ['reaction', 'clicker', 'memory'] as const;
const DIFFICULTIES = ['easy', 'medium', 'hard', 'expert'] as const;
const MECHANICS = ['tap', 'timer', 'combo', 'difficulty-ramp', 'moving-target', 'sequence', 'lives'] as const;

interface GameSpecForm {
  template: string;
  theme: string;
  difficulty: string;
  duration: number;
  mechanics: string[];
}

interface ArtifactDisplay {
  manifest: Record<string, unknown>;
  source: string;
  config: Record<string, unknown>;
  tests: string;
  metadata: Record<string, unknown>;
}

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  DRAFT: { bg: '#4b5563', fg: '#ffffff' },
  BUILDING: { bg: '#2563eb', fg: '#ffffff' },
  TESTING: { bg: '#ca8a04', fg: '#ffffff' },
  APPROVED: { bg: '#16a34a', fg: '#ffffff' },
  PUBLISHED: { bg: '#059669', fg: '#ffffff' },
  PAUSED: { bg: '#ea580c', fg: '#ffffff' },
  KILLED: { bg: '#dc2626', fg: '#ffffff' },
};

export default function AdminCreatorPage() {
  const [spec, setSpec] = useState<GameSpecForm>({
    template: 'reaction',
    theme: 'space',
    difficulty: 'medium',
    duration: 30,
    mechanics: ['tap', 'timer'],
  });

  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'success' | 'failed'>('idle');
  const [artifact, setArtifact] = useState<ArtifactDisplay | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  const [gameVersion, setGameVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [repairCount, setRepairCount] = useState(0);
  const [buildStatus, setBuildStatus] = useState<string>('not_run');
  const [qaStatus, setQaStatus] = useState<string>('not_run');
  const [failureReasons, setFailureReasons] = useState<string[]>([]);
  const [rollbackVersion, setRollbackVersion] = useState('');

  const toggleMechanic = (mechanic: string) => {
    setSpec((prev) => ({
      ...prev,
      mechanics: prev.mechanics.includes(mechanic)
        ? prev.mechanics.filter((m) => m !== mechanic)
        : [...prev.mechanics, mechanic],
    }));
  };

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setGenerationStatus('generating');
    setError(null);
    setActionMessage(null);
    setArtifact(null);
    setGameId(null);
    setRepairCount(0);
    setBuildStatus('not_run');
    setQaStatus('not_run');
    setFailureReasons([]);

    try {
      const result = await creatorApi.generate({
        template: spec.template,
        theme: spec.theme,
        difficulty: spec.difficulty,
        duration: spec.duration,
        mechanics: spec.mechanics,
        monetization: 'none',
      });

      setArtifact(result.artifact as ArtifactDisplay);
      setGameId(result.game_id);
      setGameVersion(result.version);
      setGenerationStatus('success');
      setBuildStatus('passed');
      setQaStatus('passed');
      setActionMessage(`Game generated successfully. Version: ${result.version}`);
    } catch (err) {
      setGenerationStatus('failed');
      setError(err instanceof Error ? err.message : 'Generation failed');
      setFailureReasons([err instanceof Error ? err.message : 'Unknown error']);
    } finally {
      setGenerating(false);
    }
  }, [spec]);

  const handleRepair = useCallback(async () => {
    if (!artifact) return;
    setGenerating(true);
    try {
      const result = await creatorApi.repair(artifact, {
        build: failureReasons,
        qa: [],
      });
      setArtifact(result.artifact as ArtifactDisplay);
      setRepairCount((c) => c + 1);
      setActionMessage(`Repair attempt ${repairCount + 1} completed.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Repair failed');
    } finally {
      setGenerating(false);
    }
  }, [artifact, failureReasons, repairCount]);

  const handleApprove = useCallback(async () => {
    if (!gameId) return;
    try {
      await gamesApi.transition(gameId, 'APPROVED', {
        humanApproved: true,
        approverId: 'admin-user',
      });
      setActionMessage('Game approved by human operator.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    }
  }, [gameId]);

  const handlePublish = useCallback(async () => {
    if (!gameId) return;
    try {
      await gamesApi.transition(gameId, 'PUBLISHED', {
        humanApproved: true,
        approverId: 'admin-user',
      });
      setActionMessage('Game published by human operator.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed');
    }
  }, [gameId]);

  const handleRollback = useCallback(async () => {
    if (!gameId || !rollbackVersion) return;
    try {
      await versionsApi.rollback(gameId, rollbackVersion, 'Manual rollback from admin UI');
      setActionMessage(`Rolled back to version ${rollbackVersion}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rollback failed');
    }
  }, [gameId, rollbackVersion]);

  const manifestStatus = artifact?.manifest?.status as string | undefined;
  const badge = manifestStatus ? STATUS_COLORS[manifestStatus] ?? { bg: '#6b7280', fg: '#ffffff' } : null;

  return (
    <div style={{ padding: '24px 0' }} data-testid="admin-creator-page">
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2 style={{ fontSize: '1.5rem', margin: 0 }}>AI Game Creator</h2>
        <Link to="/admin/games" style={{ fontSize: '0.9rem', color: 'var(--color-text-muted)' }}>
          &larr; Game Admin
        </Link>
      </div>

      {actionMessage && (
        <div
          role="status"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            background: 'rgba(5, 150, 105, 0.12)',
            color: '#059669',
            fontSize: '0.9rem',
          }}
        >
          {actionMessage}
        </div>
      )}
      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            background: 'rgba(220, 38, 38, 0.12)',
            color: '#dc2626',
            fontSize: '0.9rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Game Spec Form */}
      <div
        data-testid="admin-creator-form"
        style={{
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          padding: 16,
          marginBottom: 16,
          background: 'var(--color-bg-secondary)',
        }}
      >
        <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Game Spec</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Template</label>
            <select
              value={spec.template}
              onChange={(e) => setSpec({ ...spec, template: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border)' }}
            >
              {TEMPLATES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Theme</label>
            <input
              type="text"
              value={spec.theme}
              onChange={(e) => setSpec({ ...spec, theme: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Difficulty</label>
            <select
              value={spec.difficulty}
              onChange={(e) => setSpec({ ...spec, difficulty: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border)' }}
            >
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Duration (seconds)</label>
            <input
              type="number"
              value={spec.duration}
              onChange={(e) => setSpec({ ...spec, duration: Number(e.target.value) })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border)' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}>Mechanics</label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {MECHANICS.map((m) => (
              <label key={m} style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={spec.mechanics.includes(m)}
                  onChange={() => toggleMechanic(m)}
                />
                {m}
              </label>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <button
            data-testid="generate-btn"
            onClick={() => void handleGenerate()}
            disabled={generating}
            style={{
              padding: '8px 24px',
              borderRadius: 'var(--radius)',
              border: 'none',
              background: generating ? '#6b7280' : 'var(--color-primary)',
              color: '#ffffff',
              cursor: generating ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            {generating ? 'Generating...' : 'Generate Game'}
          </button>
          <span style={{ marginLeft: 12, fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            Status: {generationStatus}
          </span>
        </div>
      </div>

      {/* GameArtifact Display */}
      {artifact && (
        <div
          data-testid="artifact-display"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            marginBottom: 16,
            background: 'var(--color-bg-secondary)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: '1.1rem', margin: 0 }}>GameArtifact</h3>
            {badge && (
              <span
                style={{
                  background: badge.bg,
                  color: badge.fg,
                  borderRadius: 999,
                  padding: '2px 10px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {manifestStatus}
              </span>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: '0.85rem' }}>
            <div><strong>Game ID:</strong> {gameId ?? '—'}</div>
            <div><strong>Version:</strong> {gameVersion ?? '—'}</div>
            <div><strong>Slug:</strong> {String(artifact.manifest?.slug ?? '—')}</div>
            <div><strong>Title:</strong> {String(artifact.manifest?.title ?? '—')}</div>
            <div><strong>Template:</strong> {String(artifact.metadata?.template ?? '—')}</div>
            <div><strong>Theme:</strong> {String(artifact.metadata?.theme ?? '—')}</div>
            <div><strong>Source length:</strong> {artifact.source.length} chars</div>
            <div><strong>Tests length:</strong> {artifact.tests.length} chars</div>
            <div><strong>Repair attempts:</strong> {repairCount}</div>
            <div><strong>Generated at:</strong> {String(artifact.metadata?.generated_at ?? '—')}</div>
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 16, fontSize: '0.85rem' }}>
            <span><strong>Build:</strong> <span style={{ color: buildStatus === 'passed' ? '#059669' : '#dc2626' }}>{buildStatus}</span></span>
            <span><strong>QA:</strong> <span style={{ color: qaStatus === 'passed' ? '#059669' : '#dc2626' }}>{qaStatus}</span></span>
          </div>

          {failureReasons.length > 0 && (
            <div style={{ marginTop: 12, padding: 8, background: 'rgba(220,38,38,0.08)', borderRadius: 4 }}>
              <strong style={{ fontSize: '0.85rem' }}>Failure Reasons:</strong>
              <ul style={{ margin: '4px 0 0 16px', fontSize: '0.8rem', color: '#dc2626' }}>
                {failureReasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              data-testid="repair-btn"
              onClick={() => void handleRepair()}
              disabled={generating || repairCount >= 3}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text)',
                cursor: generating || repairCount >= 3 ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
              }}
            >
              Repair ({repairCount}/3)
            </button>
            <button
              data-testid="approve-btn"
              onClick={() => void handleApprove()}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius)',
                border: 'none',
                background: '#16a34a',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Approve (Human)
            </button>
            <button
              data-testid="publish-btn"
              onClick={() => void handlePublish()}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius)',
                border: 'none',
                background: 'var(--color-primary)',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              Publish (Human)
            </button>
          </div>
        </div>
      )}

      {/* Rollback Section */}
      {gameId && (
        <div
          data-testid="rollback-section"
          style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 16,
            background: 'var(--color-bg-secondary)',
          }}
        >
          <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Version & Rollback</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="target version (e.g. 0.1.0)"
              value={rollbackVersion}
              onChange={(e) => setRollbackVersion(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid var(--color-border)', fontSize: '0.85rem' }}
            />
            <button
              data-testid="rollback-btn"
              onClick={() => void handleRollback()}
              disabled={!rollbackVersion}
              style={{
                padding: '6px 16px',
                borderRadius: 'var(--radius)',
                border: '1px solid #ea580c',
                background: 'transparent',
                color: '#ea580c',
                cursor: !rollbackVersion ? 'not-allowed' : 'pointer',
                fontSize: '0.85rem',
              }}
            >
              Rollback
            </button>
          </div>
          <p style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            Rollback creates a new version record and writes an audit log entry. It does not overwrite history.
          </p>
        </div>
      )}
    </div>
  );
}
